import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, X } from "lucide-react";
import { api } from "@/lib/sleek";

function genSession() {
  let s = localStorage.getItem("sleek_chat_session");
  if (!s) {
    s = "s-" + (window.crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now());
    localStorage.setItem("sleek_chat_session", s);
  }
  return s;
}

export default function AIChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm the Sleek Design Assistant. Tell me a little about the space you're thinking about — kitchen, bath, or whole home?",
    },
  ]);
  const sessionId = useRef(genSession());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry — the assistant is unavailable right now. Please try again, or use the contact form." },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 group"
        aria-label="Open AI Design Assistant"
      >
        <span className="block bg-zinc-950 text-white px-5 py-4 shadow-2xl hover:bg-zinc-800 transition-colors flex items-center gap-2">
          {open ? <X size={18} /> : <Sparkles size={18} />}
          <span className="text-sm tracking-wide">{open ? "Close" : "Design Assistant"}</span>
        </span>
      </button>

      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-md bg-white border border-zinc-200 shadow-2xl flex flex-col"
          style={{ height: "min(70vh, 620px)" }}
        >
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
            <Sparkles size={16} />
            <div>
              <div className="font-display font-medium text-sm">Sleek Design Assistant</div>
              <div className="text-[11px] text-zinc-500">Powered by AI · always free to use</div>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={`${m.role}-${i}`}
                className={`text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user"
                    ? "ml-8 bg-zinc-950 text-white px-4 py-3"
                    : "mr-8 bg-zinc-100 text-zinc-900 px-4 py-3"
                }`}
                data-testid={`chat-msg-${m.role}`}
              >
                {m.content}
              </div>
            ))}
            {sending && (
              <div className="mr-8 bg-zinc-100 text-zinc-500 px-4 py-3 text-sm italic">Thinking…</div>
            )}
          </div>
          <div className="border-t border-zinc-200 p-3 flex gap-2">
            <input
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about kitchens, baths, timelines, budgets…"
              className="flex-1 bg-transparent border-b border-zinc-300 px-2 py-2 text-sm focus:outline-none focus:border-zinc-950"
            />
            <button
              onClick={send}
              disabled={sending}
              data-testid="ai-chat-send"
              className="bg-zinc-950 text-white px-3 py-2 hover:bg-zinc-800 disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
